import { Box, Text, Badge, Avatar } from '@mantine/core';
import { resolveAvatarSrc, getAvatarInitials, reportAvatarLoadError } from '@/utils/avatar';


export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  typeName?: string;
  typeColor?: string;
  activeAssignmentsCount?: number;
  activeCaseCount?: number;
  needCount?: number;
  locationsCount?: number;
  coursesCount?: number;
  isActive?: boolean;
  /**
   * Set on people who are assigned to work but have no user in Identity, so no leader or facilitator
   * record either. They replace the Active/Inactive pill, because "Inactive" would read as a deliberate
   * setting rather than a missing account.
   */
  unlistedNote?: string;
}

export interface ContactCardProps {
  contact: Contact;
  showActiveCases?: boolean;
  showActiveAssignments?: boolean;
  showNeedCount?: boolean;
  showLocationsCount?: boolean;
  showCoursesCount?: boolean;
  hideStatusBadge?: boolean;
  onClick?: (contact: Contact) => void;
}


export function ContactCard({ contact, showActiveCases = false, showActiveAssignments = false, showNeedCount = false, showLocationsCount = false, showCoursesCount = false, hideStatusBadge = false, onClick }: ContactCardProps) {
  const {
    firstName,
    lastName,
    avatar,
    typeName,
    typeColor = '#2196F3',

    activeCaseCount,
    activeAssignmentsCount,
    needCount,
    locationsCount,
    coursesCount,
    isActive,
    unlistedNote,
  } = contact;

  const fullName = `${firstName} ${lastName}`.trim();
  const avatarSrc = resolveAvatarSrc(avatar);


  return (
    <Box
      onClick={onClick ? () => onClick(contact) : undefined}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        borderRadius: 16,
        border: '1px solid var(--mantine-color-gray-2)',
        backgroundColor: 'var(--mantine-color-body)',
        padding: 20,
      }}
    >
      {/* Status pill, top-left. An unlisted person gets the missing-account note instead. */}
      {unlistedNote ? (
        <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
          <Badge size="sm" radius="xl" variant="light" color="orange">
            {unlistedNote}
          </Badge>
        </Box>
      ) : !hideStatusBadge && (
        <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 14 }}>
          <Badge
            size="sm"
            radius="xl"
            variant="light"
            color={isActive ? 'green' : 'gray'}
            leftSection={
              <Box
                w={6}
                h={6}
                style={{
                  borderRadius: '50%',
                  backgroundColor: isActive ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-5)',
                }}
              />
            }
          >
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        </Box>
      )}

      {/* Avatar + name + role, centered up top (no banner) */}
      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 16 }}>
        {/* Initials are always the children so a broken image URL falls back to them
            rather than Mantine's generic placeholder. */}
        <Avatar
          src={avatarSrc}
          alt={fullName}
          radius="xl"
          size={72}
          color="gray"
          mb={12}
          imageProps={{ onError: () => reportAvatarLoadError(avatarSrc) }}
        >
          {getAvatarInitials(firstName, lastName)}
        </Avatar>
        <Text fw={600} size="lg" mb={8}>
          {fullName}
        </Text>
        {typeName && (
          <Badge
            size="sm"
            radius="xl"
            variant="light"
            style={{ backgroundColor: `${typeColor}22`, color: typeColor, textTransform: 'none' }}
          >
            {typeName}
          </Badge>
        )}
      </Box>

        {showActiveCases && activeCaseCount !== undefined && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text size="xs" c="dimmed">
              Active cases
            </Text>
            <Badge size="sm" variant="light" color="gray" radius="xl">
              {activeCaseCount}
            </Badge>
          </Box>
        )}
         {showActiveAssignments && activeAssignmentsCount !== undefined && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text size="xs" c="dimmed">
              Active enrolment
            </Text>
            <Badge size="sm" variant="light" color="gray" radius="xl">
              {activeAssignmentsCount}
            </Badge>
          </Box>
        )}
         {showNeedCount && needCount !== undefined && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <Text size="xs" c="dimmed">
              Needs
            </Text>
            <Badge size="sm" variant="light" color="gray" radius="xl">
              {needCount}
            </Badge>
          </Box>
        )}
         {showLocationsCount && locationsCount !== undefined && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <Text size="xs" c="dimmed">
              Locations
            </Text>
            <Badge size="sm" variant="light" color="gray" radius="xl">
              {locationsCount}
            </Badge>
          </Box>
        )}
         {showCoursesCount && coursesCount !== undefined && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <Text size="xs" c="dimmed">
              Courses
            </Text>
            <Badge size="sm" variant="light" color="gray" radius="xl">
              {coursesCount}
            </Badge>
          </Box>
        )}

    </Box>
  );
}